import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, TrendingUp, TrendingDown, Award, AlertTriangle, Loader2, ChevronDown, Info } from 'lucide-react';
import { metricsAPI, handleMetricsAPIError, metricsFormatters } from './api';

const GroupRatingsView = ({ isDark, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratingsData, setRatingsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [sortFilter, setSortFilter] = useState('top'); // top, low, declining, improving
  const [showLegend, setShowLegend] = useState(false);

  // Generate month options from Oct 2024 to current month
  const generateMonthOptions = () => {
    const months = [];
    const startDate = new Date(2024, 9, 1); // October 2024
    const currentDate = new Date();
    
    let date = new Date(startDate);
    while (date <= currentDate) {
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthStr = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const value = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      months.push({ label: monthStr, value });
      date.setMonth(date.getMonth() + 1);
    }
    
    return months.reverse(); // Most recent first
  };

  const monthOptions = generateMonthOptions();

  useEffect(() => {
    // Set default to current month
    if (monthOptions.length > 0) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchRatings();
    }
  }, [selectedMonth]);

  useEffect(() => {
    applyFilters();
  }, [ratingsData, searchQuery, sortFilter]);

  const fetchRatings = async () => {
    setLoading(true);
    setError(null);
  
    try {
      // Parse the selected month string to extract year and month
      const [year, month] = selectedMonth.split('-').map(Number);
      
      const response = await metricsAPI.getGroupRatings({
        year: year,      // e.g., 2024
        month: month     // e.g., 11 for November
      });
  
      if (response.success) {
        // Handle both array and object responses
        const data = Array.isArray(response.data) ? response.data : (response.data?.groups || []);
        setRatingsData(data);
      } else {
        setError('Failed to load group ratings');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // Ensure ratingsData is an array
    if (!Array.isArray(ratingsData)) {
      setFilteredData([]);
      return;
    }

    let filtered = [...ratingsData];

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(group =>
        group.group_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sort filter
    switch (sortFilter) {
      case 'top':
        filtered.sort((a, b) => b.current_month.scores.total - a.current_month.scores.total);
        break;
      case 'low':
        filtered.sort((a, b) => a.current_month.scores.total - b.current_month.scores.total);
        break;
      case 'declining':
        filtered.sort((a, b) => {
          const changeA = a.changes?.month_to_month || 0;
          const changeB = b.changes?.month_to_month || 0;
          return changeA - changeB; // Most negative first
        });
        break;
      case 'improving':
        filtered.sort((a, b) => {
          const changeA = a.changes?.month_to_month || 0;
          const changeB = b.changes?.month_to_month || 0;
          return changeB - changeA; // Most positive first
        });
        break;
      default:
        break;
    }

    setFilteredData(filtered);
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'text-green-600 bg-green-100';
    if (grade.startsWith('B')) return 'text-blue-600 bg-blue-100';
    if (grade.startsWith('C')) return 'text-orange-600 bg-orange-100';
    if (grade.startsWith('D')) return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <TrendingUp className="w-4 h-4" />;
    if (change < 0) return <TrendingDown className="w-4 h-4" />;
    return null;
  };

  if (loading) {
    return (
      <div>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance
        </button>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading group ratings...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance
        </button>
        <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
          <button 
            onClick={fetchRatings}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Group Performance Ratings
            </h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              Monthly ratings based on 6 key metrics (100 points total)
            </p>
          </div>
          <button
            onClick={() => setShowLegend(!showLegend)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <Info className="w-4 h-4" />
            <span className="text-sm">Legend</span>
          </button>
        </div>

        {/* Legend */}
        {showLegend && (
          <div className={`mb-4 p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Column Abbreviations:
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><span className="font-semibold">CR:</span> Contribution Rate (15 pts)</div>
              <div><span className="font-semibold">MS:</span> Minimum Savings (10 pts)</div>
              <div><span className="font-semibold">AI:</span> Advance Interest (20 pts)</div>
              <div><span className="font-semibold">LR:</span> Loan Repayment (35 pts)</div>
              <div><span className="font-semibold">DF:</span> Defaulters (10 pts)</div>
              <div><span className="font-semibold">DR:</span> Default Reduction (10 pts)</div>
              <div><span className="font-semibold">MoM:</span> Month-over-Month Change</div>
              <div><span className="font-semibold">Cum Avg:</span> Cumulative Average</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2 relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark 
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Sort Filter */}
          <div className="relative">
            <select
              value={sortFilter}
              onChange={(e) => setSortFilter(e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg appearance-none transition-colors ${
                isDark 
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="top">Top Rated</option>
              <option value="low">Low Rated</option>
              <option value="improving">Most Improving</option>
              <option value="declining">Most Declining</option>
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>

          {/* Month Filter */}
          <div className="relative">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg appearance-none transition-colors ${
                isDark 
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Groups</div>
          <div className="text-2xl font-bold text-blue-600">{filteredData.length}</div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>A Grade</div>
          <div className="text-2xl font-bold text-green-600">
            {filteredData.filter(g => g.current_month.scores.grade?.startsWith('A')).length}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-800' : 'bg-orange-50 border border-orange-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>Avg Score</div>
          <div className="text-2xl font-bold text-orange-600">
            {filteredData.length > 0 
              ? (filteredData.reduce((sum, g) => sum + g.current_month.scores.total, 0) / filteredData.length).toFixed(1)
              : '0'
            }
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>Below 60</div>
          <div className="text-2xl font-bold text-red-600">
            {filteredData.filter(g => g.current_month.scores.total < 60).length}
          </div>
        </div>
      </div>

      {/* Ratings Table */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>#</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Group</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Grade</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>CR</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>MS</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>AI</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>LR</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>DF</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>DR</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>MoM</th>
                <th className={`px-3 py-3 text-center text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Cum Avg</th>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Officers</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredData.map((group, index) => (
                <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {index + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {group.group_name}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {group.current_month.period_name}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getGradeColor(group.current_month.scores.grade || 'F')}`}>
                      {group.current_month.scores.grade || 'F'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {group.current_month.scores.total.toFixed(1)}
                    </span>
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.current_month.scores.contribution.toFixed(1)}
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.current_month.scores.minimum_savings.toFixed(1)}
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.current_month.scores.advance_interest.toFixed(1)}
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.current_month.scores.loan_repayment.toFixed(1)}
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.current_month.scores.defaulters.toFixed(1)}
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.current_month.scores.default_reduction.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <div className={`flex items-center justify-center gap-1 text-sm font-semibold ${getChangeColor(group.changes?.month_to_month || 0)}`}>
                      {getChangeIcon(group.changes?.month_to_month || 0)}
                      <span>{Math.abs(group.changes?.month_to_month || 0).toFixed(1)}</span>
                    </div>
                  </td>
                  <td className={`px-3 py-3 text-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {group.cumulative_average?.scores?.total?.toFixed(1) || 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {group.current_month.officers?.map((officer, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                          title={officer.officer_name}
                        >
                          {officer.initials}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredData.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No groups found matching your criteria
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupRatingsView;